/**
 * LureLight — the one point light in a game that otherwise has exactly one directional.
 *
 * Everything in this world is lit by a single key light swept round by DayNight: sun by day,
 * moon by night, one direction and one colour fanned out to every material by `applyEnv`. That
 * is the whole lighting model, and it is enough for an ocean lit from above.
 *
 * The anglerfish breaks it. Its esca is the only light where it lives, and a lure that merely
 * GLOWS is a bright dot pasted on a silhouette — it has to rake the sand, pick out the teeth,
 * and catch the flank of a shark that swims past it, or none of it reads. So this adds a
 * genuine point light, on the same structural pattern DayNight uses: a small uniform set, a
 * GLSL helper, and a push function that quietly does nothing to any material that has not
 * opted in by declaring the uniforms.
 *
 * ONE LURE, NOT N. Supporting an array would put a loop in every fragment shader in the game
 * for a light that is usually not on screen at all. Instead the nearest lit esca wins and the
 * rest are emissive-only. Anglers are rare and spread along the drop-off, so being within reach
 * of two at once is a situation that essentially does not arise — and when it does, the far one
 * is still glowing, it just is not casting.
 */
import * as THREE from 'three';

export interface LureState {
  /** world position of the esca */
  x: number;
  y: number;
  z: number;
  /** 0 when nothing is near enough to bother lighting */
  power: number;
  /** how far it throws, world units */
  reach: number;
  color: THREE.Color;
}

export function makeLureState(): LureState {
  return { x: 0, y: -999, z: 0, power: 0, reach: 9, color: new THREE.Color(0.42, 1.0, 0.86) };
}

/** the uniforms a material must declare to receive the lure */
export function lureUniforms(): Record<string, THREE.IUniform> {
  return {
    uLurePos: { value: new THREE.Vector3(0, -999, 0) },
    uLureCol: { value: new THREE.Vector3(0.42, 1.0, 0.86) },
    uLurePower: { value: 0 },
    uLureReach: { value: 9 },
  };
}

/**
 * GLSL. Include in a fragment shader, then add `lureLight(worldPos, normal)` to the colour.
 *
 * Inverse-square with a constant floor, which is the physically right falloff and also the one
 * that makes the light feel like it has a body: a lamp that falls off linearly reads as a
 * gradient painted on the floor, not as something in the water.
 */
export const LURE_GLSL = /* glsl */ `
uniform vec3  uLurePos;
uniform vec3  uLureCol;
uniform float uLurePower;
uniform float uLureReach;

vec3 lureLight(vec3 wpos, vec3 nrm) {
  if (uLurePower < 0.001) return vec3(0.0);
  vec3 d = uLurePos - wpos;
  float r = max(length(d), 1e-3);
  vec3 L = d / r;
  float atten = 1.0 / (1.0 + (r * r) * (3.4 / max(uLureReach, 0.05)));
  /* A floor on the normal term, not a clamp to zero. A surface facing away from the lure is
     still in its glow — the water scatters — and cutting it to black puts a hard terminator
     across the sand that reads as a shadow with no caster. */
  float ndl = 0.20 + 0.80 * max(dot(normalize(nrm), L), 0.0);
  return uLureCol * uLurePower * atten * ndl;
}
`;

/**
 * Push the lure into any material that speaks these uniform names.
 *
 * Structural rather than an interface, exactly as applyEnv is: a new material joins the lure
 * lighting by declaring the uniforms and doing nothing else.
 */
export function applyLure(u: Record<string, THREE.IUniform> | undefined, lure: LureState): void {
  if (!u || !u.uLurePos) return;
  (u.uLurePos.value as THREE.Vector3).set(lure.x, lure.y, lure.z);
  const c = u.uLureCol?.value as THREE.Vector3 | undefined;
  if (c) c.set(lure.color.r, lure.color.g, lure.color.b);
  if (u.uLurePower) u.uLurePower.value = lure.power;
  if (u.uLureReach) u.uLureReach.value = lure.reach;
}
